<template>
  <div
    v-click-outside="clickOutsideHandler"
    class="list-wrap"
    :class="{
      'invite-members': isInviteMembersDropdown,
      'hide-scroll-panel': isInviteMembersDropdown && !filterInviteMembers.length,
    }"
    @click.stop
    @mousewheel="scrollHandle"
    @mousemove="mouseMoveHandle"
    @keydown.esc="scrollToTop"
    @mousedown="focusElementHandler"
    @mouseup="blurElementHandler"
  >
    <div
      class="members-header-wrap members-search usn"
      @click.stop="focusInput"
    >
      <input
        ref="searchInput"
        v-model="searchValue"
        v-autofocus="!isMobile"
        class="members-header"
        type="text"
        :placeholder="inputPlaceholder"
        @keyup="checkKey"
        @compositionupdate="compositionUpdate"
        @keydown="checkDownKey"
      />
      <ShortcutKeyPill
        v-if="showShortcutPill"
        class="absolute top-3 right-4"
        :text="'A'"
        :background-color="key.background"
        :font-color="key.text"
        :border-color="key.border"
      />
    </div>
    <vue-scroll
      ref="membersScroll"
      class="members-list"
      :class="{ scrolling: scrolling }"
      :ops="{
        scrollPanel: { scrollingX: false },
        bar: { keepShow: true, size: '4px' },
        rail: { gutterOfSide: '2px' },
      }"
      @handle-scroll="handleScroll"
    >
      <div
        v-if="membersNotFound && !singleSelect && !isAttendeesDropdown"
        class="members-notfound-wrap"
      >
        <p>{{ translate('notWorkspaceMember') }}</p>
        <span v-if="isElevated">
          <span> - </span>
          <span
            class="members-notfound"
            @click="displayInvitePopup"
          >
            {{ translate('inviteExternalPeople') }}</span
          >
        </span>
      </div>
      <div
        v-for="(member, index) in memberItems"
        :key="getMemberKey(member)"
        ref="membersDraggableItem"
        class="members-dropdown-list-item"
        @mousedown.prevent
      >
        <FilterDropdownItem
          :index="index"
          :item="member"
          :items="memberItems"
          :selected-item-index="selectedIndex"
          :disabled="
            (isInactiveMember(member.id) && !member.checked) ||
            (isGuestRole && !isGuestAddedToSpace) ||
            member.disabled
          "
          :hover-preview-disabled="false"
          @item-hover="itemHover(index)"
          @scroll-to-item="scrollToItem"
          @item-click="memberClick(member)"
          @item-right-side-click="itemRightSideClick"
          @more-click="handleMoreClick(index)"
        />
        <v-popover
          v-if="member.showMore"
          ref="moreOptionsPopover"
          interactive
          tag="div"
          content-tag="div"
          class="w-full"
          :role="'poper'"
          :trigger="'manual'"
          :placement="'right-start'"
          :offset="[-40, 8]"
          :append-to="() => documentBody"
          :on-hide="() => $emit('onMoreOptionsHide')"
        >
          <template #content="{ state, hide }">
            <slot
              :state="state"
              :member="member"
              :hide="hide"
              name="moreOptions"
            />
          </template>
        </v-popover>
      </div>
    </vue-scroll>
    <MembersDropdownInviteSection
      v-if="isInviteMembersDropdown"
      :class="{ hidden: !isElevated }"
      @handleEsc="handleEsc"
    />
    <Portal
      v-if="isConfirmationRevealed"
      to="root-widget"
    >
      <DoubleCheck
        v-click-outside="cancelConfirmation"
        :member="$store.getters.getUser"
        :members-array="currentProject.members"
        :project="project"
        :show-user-info="false"
        :user-data="currentUser"
        @closeDoubleCheckPopup="cancelConfirmation"
        @leaveSpaceConfirmed="fulfilConfirmation"
      />
    </Portal>
  </div>
</template>

<script>
import draggableMixin from '@/mixins/draggable';
import headerMixin from '@/mixins/headerMixin';
import inputDropdown from '@/mixins/inputDropdown';
import filterMembers from '@/utilities/filterMembers';
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import EventBus from '@/utilities/eventBus';
import getMemberLabel from '@/utilities/getMemberLabel';
import FilterDropdownItem from '@/components/widgets/filters/FilterDropdownItem';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import ShortcutKeyPill from '@/components/functional/ShortcutKeyPill';
import defaultShortcutKeyPillStyle from '@/mixins/defaultShortcutKeyPillStyle';
import DoubleCheck from './DoubleCheck.vue';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import MembersDropdownInviteSection from '@/components/elements/MembersDropdownInviteSection';
import getCurrentDateInSeconds from '@/utilities/getCurrentDateInSeconds';
import { ResourceType } from '@/types/resources';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import useManageCardMembers from '@/boards/composables/useManageCardMembers';
import { TeamMemberStatus, TeamMemberRole } from '@superthread-com/api';
import { ref } from 'vue';

export default {
  name: 'MembersDropdownList',
  components: {
    FilterDropdownItem,
    ShortcutKeyPill,
    Portal,
    DoubleCheck,
    MembersDropdownInviteSection,
  },
  mixins: [
    draggableMixin,
    headerMixin,
    inputDropdown,
    elementFocusMixin,
    defaultShortcutKeyPillStyle,
  ],
  props: {
    entity: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    members: {
      type: Array,
      required: false,
      default: () => [],
    },
    item: {
      type: Object,
      required: false,
      default: () => {},
    },
    singleSelect: {
      type: Boolean,
      required: false,
      default: false,
    },

    childCardItem: {
      type: Boolean,
      required: false,
      default: false,
    },

    showShortcutPill: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * this prop is used when members dropdown is used in embedded boards
     * and it presents the type of the resource on which the members dropdown is used
     */
    resourceType: {
      type: String,
      required: false,
      default: '',
    },

    isQuickCardModal: {
      type: Boolean,
      required: false,
      default: false,
    },

    entityTypeProp: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['closeDropdown', 'memberClick', 'selected-item', 'clickOutside', 'onMoreOptionsHide'],
  setup() {
    const { setAndOpenAdjacentProject } = useSetAndOpenProject();
    const { isRevealed, reveal, confirm, cancel, onConfirm } = useConfirmDialog();
    const { currentRole, isElevated, isOwner, isAdmin } = useCurrentRole();
    const { unassignMember, assignMember } = useManageCardMembers();

    const documentBody = ref(document.body);

    return {
      isConfirmationRevealed: isRevealed,
      revealConfirmation: reveal,
      cancelConfirmation: cancel,
      onConfirmationDone: onConfirm,
      fulfilConfirmation: confirm,
      setAndOpenAdjacentProject,
      currentRole,
      isElevated,
      isOwner,
      isAdmin,
      unassignMember,
      assignMember,
      documentBody,
    };
  },
  data() {
    return {
      searchValue: '',
      inProjectMembers: [],
      notInProjectMembers: [],
      focusTimeout: null,
      selectedIndex: 0,
      scrolling: false,
      scrollTimeout: null,
      cancelledRemoveMeUser: false,
      currentMoreOptionsPopoverIndex: -1,
    };
  },
  computed: {
    settingsCurrentProjectId() {
      return this.$store.getters.getSettingsCurrentProjectId;
    },
    settingsCurrentProject() {
      return this.$store.getters.getProjectById(this.settingsCurrentProjectId) || {};
    },
    isSettings() {
      return this.$route.path.includes('/settings/');
    },
    displayCheckboxes() {
      return !(this.isGuestRole && this.entityType.includes('project'));
    },
    isManageSpaces() {
      return [
        this.$constants.manageSpacesRouteName,
        this.$constants.settingsManageSpacesRouteName,
      ].includes(this.$route.name);
    },
    isFirstProjectInManageProjects() {
      return this.isManageSpaces && this.entityType.split('-')[1] === '1';
    },
    isFirstSpace() {
      return this.entityType === 'project' && this.isProject
        ? this.currentProjectId === '1'
        : this.isFirstProjectInManageProjects;
    },
    invitees() {
      return this.$store.getters.getMembersDropdownInvitees;
    },
    isInviteMembersDropdown() {
      return this.entityType === 'invite-members';
    },
    currentUser() {
      return this.user[0];
    },
    membersToAddQuickCard() {
      return this.isQuickCardModal
        ? this.$store.getters.getMembersToAddQuickCard
        : this.$store.getters.getMembersToAddQuickChildCard;
    },
    memberItems() {
      if (this.isInviteMembersDropdown) {
        return this.filterInviteMembers.map((m) => ({
          avatar: true,
          id: m.id,
          lighter: true,
          checkbox: true,
          checked: this.members.some((i) => i.user_id === m.id || this.isInvited(m.id)),
          display_name: this.getMemberDisplay(m),
          label: this.getMemberDisplay(m),
        }));
      }
      if ([this.$constants.quickCard, this.$constants.quickChildCard].includes(this.entityType)) {
        return this.getTeamMembersWithMeFirst.map((m) => ({
          avatar: true,
          id: m.id,
          lighter: true,
          checkbox: true,
          checked: this.membersToAddQuickCard.some((mc) => mc.user_id === m.id),
          display_name: this.getMemberDisplay(m),
          label: this.getMemberDisplay(m),
        }));
      }
      if (this.entityType === this.$constants.settingsTemplate.root) {
        return this.getTeamMembers.map((m) => ({
          avatar: true,
          id: m.id,
          lighter: true,
          checkbox: true,
          checked: this.members.some((i) => i.user_id === m.id),
          display_name: this.getMemberDisplay(m),
          label: this.getMemberDisplay(m),
        }));
      }
      if (this.isAttendeesDropdown) {
        return this.members
          .filter((m) => m.email.includes(this.searchValue))
          .map((m) => {
            const member = this.getTeamMembers.find((tm) => tm.id === m?.id);
            const label = !member?.id ? m.email : this.getMemberDisplay(member);

            const isWorkspaceMember = !!m.id;

            return {
              avatar: m.id,
              id: m.id,
              lighter: true,
              display_name: label,
              email: m.email,
              label,
              checked: m.checked,
              checkbox: isWorkspaceMember,
              preventClick: !isWorkspaceMember,
              showMore: !isWorkspaceMember,
              groupingItem: m.groupingItem,
              groupLabel: m.groupLabel,
              ...(!isWorkspaceMember && { resourceIcon: { src: 'mail' } }),
            };
          });
      }

      return this.filterInProjectMembers.map((member) => {
        const { canModify, reason } = this.canModifySpaceMember(member);
        return {
          avatar: true,
          id: member.id,
          lighter: true,
          label: this.getMemberDisplay(member),
          role: member.role,
          checkbox: this.displayCheckboxes,
          checked:
            this.isDetailsSidebar || this.isEpic
              ? this.members?.some?.((i) => i.user_id === member.id)
              : member.checked,
          groupingItem: member.groupingItem,
          ...(member.groupingItem && { groupLabel: member.groupLabel }),
          ...(!canModify && { disabled: !canModify, showTooltip: !canModify, tooltipText: reason }),
        };
      });
    },

    isEpic() {
      return this.entityType.startsWith(`${ResourceType.Epic}-`);
    },

    isDetailsSidebar() {
      return (
        this.entityType.startsWith(this.$cons.cardDetailsSidebar) ||
        this.entityType.startsWith(this.$cons.epicDetailsSidebar)
      );
    },

    isDetailsSidebarOwner() {
      return this.isDetailsSidebar && this.entityType.includes(this.$cons.owner);
    },

    searchResults() {
      return this.memberItems;
    },

    inputPlaceholder() {
      if (this.isInviteMembersDropdown) {
        return this.translate('searchMembers');
      }
      if (this.entityType.includes('project')) {
        return this.isFirstSpace ? this.translate('findMembers') : this.translate('addMembers');
      }
      if (this.isAttendeesDropdown) {
        return `${this.translate('attendees')}...`;
      }
      let assignees = this.translate('assignees');
      if (this.$store.getters.getCurrentCardIdMemberDropdown.includes(this.isDetailsSidebarOwner))
        assignees = this.translate('setOwner');
      return `${assignees.charAt(0).toUpperCase()}${assignees.slice(1)}...`;
    },

    membersWithoutMe() {
      const meUserId = this.$store.getters.getUserId;
      return this.getTeamMembers.filter(({ id }) => id !== meUserId);
    },

    filterInProjectMembers() {
      return filterMembers(this.inProjectMembers, this.searchValue);
    },

    filterInviteMembers() {
      return filterMembers(this.membersWithoutMe, this.searchValue);
    },

    getUser() {
      return this.$store.getters.getUser;
    },

    user() {
      const user = { ...this.getUser };
      return [
        {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          profile_image: user.profile_image,
          color: user.color,
          display_name: user.display_name,
          email: user.email,
          checked: this.meChecked,
          ...(!this.isEpic && {
            groupingItem: true,
            groupLabel: this.meGroupLabel,
          }),
        },
      ];
    },

    meGroupLabel() {
      if (this.entityType.includes('project') && !this.meChecked) {
        return this.translate('notInThisSpace');
      }

      return this.translate('inThisSpace');
    },

    meFiltered() {
      return filterMembers(this.user, this.searchValue);
    },

    meChecked() {
      return this.members?.some?.((i) => i.user_id === this.getUser.id);
    },

    isCurrentUserSpaceOwner() {
      return this.entityType.includes('project') ? this.entity.user_id === this.getUser.id : false;
    },
    isCurrentUserSpaceMember() {
      return this.entityType.includes('project')
        ? this.entity.members?.some((member) => member.user_id === this.getUser.id) || false
        : false;
    },
    isProjectPrivate() {
      return this.entityType.includes('project') && !!this.entity.is_private;
    },

    getProjectMembers() {
      return this.currentProject.members || [];
    },

    getTeamMembers() {
      let teamMembers = Object.values(this.$store.getters.getTeamMembers);
      const projectMembers = this.project?.members || [];
      if (this.entityType === 'project') {
        return filterMembers(teamMembers, this.searchValue);
      }
      teamMembers = teamMembers.filter((m) => {
        const isGuest = m.role === TeamMemberRole.GuestReader || m.role === TeamMemberRole.Guest;
        const isProjectMember = projectMembers.some((pm) => pm.user_id === m.id);
        // Show member if:
        // 1. They are not a guest, OR
        // 2. They are a guest AND they are a project member
        return !isGuest || (isGuest && isProjectMember);
      });
      return filterMembers(teamMembers, this.searchValue);
    },

    // team members with me at first place
    getTeamMembersWithMeFirst() {
      return filterMembers([this.getUser, ...this.membersWithoutMe], this.searchValue);
    },

    projectMembers() {
      return this.getProjectMembers
        ? this.getTeamMembers.filter((tm) =>
            this.getProjectMembers.some((m) => m.user_id === tm.id && m.user_id !== this.getUser.id)
          )
        : this.getTeamMembers;
    },

    epicMembers() {
      const allMembers = [...this.getTeamMembers, ...Object.values(this.inactiveTeamMembers)];
      const members = allMembers.reduce((acc, member) => {
        const isMember = this.members.some((m) => m.user_id === member.id);
        const isInactive = member.status === TeamMemberStatus.Inactive;
        // filter out imported inactive users
        const isImportedInactive =
          member.status === TeamMemberStatus.Imported && (!member.email || !member.email_confirmed);

        if (isMember || (!isInactive && !isImportedInactive)) {
          acc.push({ ...member, checked: isMember });
        }

        return acc;
      }, []);

      return members.sort((a, b) => {
        if (a.id === this.getUser.id) return -1;
        if (b.id === this.getUser.id) return 1;
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        return a.assigned_date - b.assigned_date;
      });
    },

    checkedProjectMembers() {
      const inactiveTeamMembers = Object.values(this.inactiveTeamMembers);
      const allMembers = [...this.getTeamMembers, ...inactiveTeamMembers];

      return allMembers
        .map((i) => {
          const member = this.members?.find?.((m) => m.user_id === i.id);
          if (member && member.user_id !== this.getUser.id) {
            return { ...i, ...member, checked: true };
          }
          return false;
        })
        .filter(Boolean)
        .sort((a, b) => a.assigned_date - b.assigned_date);
    },

    notCheckedProjectMembers() {
      return this.projectMembers
        .filter((i) => !this.members?.some?.((m) => m.user_id === i.id))
        .map((member) => ({ ...member, checked: false }));
    },

    teamMembers() {
      return this.getProjectMembers
        ? this.getTeamMembers
            .filter(
              (i) =>
                i.id !== this.getUser.id && !this.getProjectMembers?.some((m) => m.user_id === i.id)
            )
            .filter((j) => !this.members?.some?.((c) => c.user_id === j.id))
            .map((member) => ({ ...member, checked: false }))
        : this.getTeamMembers.map((member) => ({ ...member, checked: false }));
    },

    membersNotFound() {
      return (
        this.searchNotEmpty &&
        !(this.isInviteMembersDropdown
          ? this.filterInviteMembers.length
          : this.filterInProjectMembers.length)
      );
    },

    pageId() {
      return this.$store.getters.getPage.id;
    },
    boardId() {
      return this.$store.getters.currentBoardId;
    },
    cardId() {
      return this.resourceType === ResourceType.Card ? this.item.id : '';
    },
    currentCardMembersDropdown() {
      return this.$store.getters.getCurrentCardMembersDropdown;
    },
    currentProjectId() {
      // currentCardMembersDropdown can contain card or board object that will have project_id
      // or project object that will have id
      const projectId =
        this.currentCardMembersDropdown?.project_id || this.currentCardMembersDropdown?.id;
      return projectId || this.$store.getters.getCurrentProjectId;
    },
    currentProject() {
      // currentCardMembersDropdown can contain card or board object that will have project_id
      // or project object that will have id
      const projectId =
        this.currentCardMembersDropdown?.project_id || this.currentCardMembersDropdown?.id;
      if (projectId) {
        return this.$store.getters.getProjectById(projectId) || {};
      }
      return this.$store.getters.getCurrentProject;
    },
    searchNotEmpty() {
      return this.searchValue !== '';
    },
    membersCount() {
      return this.getTeamMembers.length;
    },
    showMembersLabel() {
      return !this.membersNotFound;
    },
    addingTarget() {
      return this.$store.getters.getAddMembersTarget || {};
    },
    cardMemberIdForEdit() {
      return this.$store.getters.getCurrentCardIdMemberDropdown;
    },
    entityType() {
      return this.$store.getters.getCurrentCardIdMemberDropdown || this.entityTypeProp;
    },
    currentProjectTitle() {
      return this.currentProject.title;
    },

    inactiveTeamMembers() {
      return this.$store.getters.getInactiveTeamMembers;
    },

    isViewOrMyWork() {
      return (
        this.isViewOpen ||
        [this.$constants.myWorkRouteName, this.$constants.memberRouteName].includes(
          this.$route.name
        )
      );
    },

    isInbox() {
      return this.$route.name === this.$constants.routeNames.inbox;
    },

    isGuestAddedToSpace() {
      return (
        this.resourceType === ResourceType.Card &&
        this.isGuestRole &&
        Object.keys(this.$store.getters.getMyProjectById(this.currentProjectId) || {}).length
      );
    },
    project() {
      return this.isSettings ? this.settingsCurrentProject : this.currentProject;
    },
    isAttendeesDropdown() {
      return this.entityType === this.$constants.meetingAttendees;
    },
  },
  watch: {
    cardMemberIdForEdit(newVal) {
      if (newVal) {
        this.focusInput();
      }
    },
    entityType(newVal) {
      if (newVal !== '') {
        this.selectedIndex = 0;
        this.orderMembers();
      }
    },
  },
  mounted() {
    this.focusInput();

    this.orderMembers();
    EventBus.$on('membersDropdownClick', this.scrollToTop);
  },

  unmounted() {
    if (this.inputFocusTimeout) {
      clearTimeout(this.inputFocusTimeout);
    }
  },
  methods: {
    handleScroll() {
      if (this.scrolling) return;
      this.scrolling = true;
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.scrolling = false;
      }, 400);
      this.$refs.moreOptionsPopover?.[this.currentMoreOptionsPopoverIndex]?.hide();
    },
    scrollToTop() {
      if (this.$refs.membersScroll) {
        this.$refs.membersScroll.$el.firstChild.scrollTo(
          {
            y: '0%',
          },
          0
        );
      }
    },
    scrollToItem({ node: itemNode }) {
      const { offsetTop: nodeTop, offsetHeight: nodeHeight } = itemNode;
      const scrollBounds = this.$refs.membersScroll.$el.getBoundingClientRect();
      const { scrollTop } = this.$refs.membersScroll.getPosition();

      if (nodeTop < scrollTop) {
        this.$refs.membersScroll.scrollTo({ y: nodeTop }, 200);
        return;
      }

      if (nodeTop + nodeHeight > scrollBounds.height + scrollTop) {
        this.$refs.membersScroll.scrollTo(
          {
            y: nodeTop + nodeHeight - scrollBounds.height,
          },
          200
        );
      }
    },
    itemHover(index) {
      this.setSelectedIndex(index);
    },
    async itemRightSideClick(item) {
      if (item.preventClick) return;

      await this.memberClick(item, true);

      // dropdown should not close if confirmation modal is cancelled
      if (this.cancelledRemoveMeUser) {
        this.cancelledRemoveMeUser = false;
        return;
      }

      this.handleCloseDropdown();
    },
    async selectSearchResult(item) {
      if (item.preventClick) return;

      await this.memberClick(item, true);
      this.handleCloseDropdown();
    },
    async selectOnlySearchResult(item) {
      if (item.preventClick) return;

      await this.memberClick(item);
    },
    handleEsc(event) {
      event.stopPropagation();
      this.handleCloseDropdown();
    },
    handleCloseDropdown() {
      this.$emit('closeDropdown');
    },
    focusInput() {
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },

    async removeMember(member, immediate) {
      const memberToRemove = this.members?.find?.((i) => i.user_id === member.id);
      if (
        [this.$constants.quickCard, this.$constants.quickChildCard].includes(this.addingTarget.type)
      ) {
        this.$store.dispatch('setMembersToAddQuickCard', {
          value: {
            user_id: member.id,
            role: member.role,
            checked: false,
          },
          isModal: this.isQuickCardModal,
        });
      }
      if (this.addingTarget.type === this.$constants.settingsTemplate.root) {
        this.$store.dispatch('setMembersToCardTemplate', {
          user_id: member.id,
          role: member.role,
          checked: false,
        });
        return;
      }
      if (memberToRemove) {
        if (
          this.isAnyCardDisplayed ||
          this.isEpic ||
          this.isViewOrMyWork ||
          this.isInbox ||
          this.resourceType === ResourceType.Card
        ) {
          const cardId = this.addingTarget?.id || this.cardId;
          const delay = immediate ? 0 : undefined;
          this.unassignMember(cardId, memberToRemove, memberToRemove.user_id, delay);
          return;
        }
        if (this.isProject || this.isManageSpaces || this.entityType === 'project') {
          await this.removeMemberFromProject(memberToRemove, member.label);
          return;
        }

        if (this.isBoard) {
          this.removeMemberFromBoard(memberToRemove, immediate);
        }
      }
    },

    async removeMemberFromProject(memberToRemove, memberName) {
      const projectName = this.isSettings
        ? this.settingsCurrentProject?.title
        : this.currentProjectTitle;
      const projectId = this.isSettings ? this.settingsCurrentProjectId : this.currentProjectId;
      const currentMemberId = this.getUser.id;
      if (memberToRemove.user_id === currentMemberId) {
        const { isCanceled } = await this.revealConfirmation();
        if (isCanceled) {
          this.cancelledRemoveMeUser = true;
          return;
        }
      }

      if (memberToRemove.user_id === currentMemberId) {
        if (!this.isManageSpaces && !this.isSettings) this.setAndOpenAdjacentProject(projectId);

        this.handleCloseDropdown();
      }
      try {
        await this.$store.dispatch('removeMemberFromProject', {
          memberToRemove,
          projectId,
        });

        if (memberToRemove.user_id === currentMemberId) {
          await this.$store.dispatch('addUiNotification', {
            message: this.translate('youLeftNamedSpace', { name: projectName }),
            duration: 4000,
          });
        } else {
          this.$store.dispatch('addUiNotification', {
            message: `${memberName} ${this.translate(
              'removedFrom'
            )} ${projectName} ${this.translate('space').toLowerCase()}`,
            duration: 4000,
          });
        }
      } catch {
        this.toggleMemberChecked({ id: memberToRemove.user_id }, 'inProjectMembers');
      }
    },

    removeMemberFromBoard(memberToRemove, immediate) {
      if (this.addingTarget && this.addingTarget.type === this.$constants.routeNames.boardCard) {
        const delay = immediate ? 0 : undefined;
        this.unassignMember(this.addingTarget.id, memberToRemove, memberToRemove.user_id, delay);
        return;
      }
      this.$store.dispatch('removeMemberFromBoard', {
        memberToRemove,
        boardId: this.boardId,
      });
    },

    async addMembers(member, immediate) {
      switch (this.addingTarget.type) {
        case this.$constants.infoSidebarProject:
        case 'project':
          const projectName = this.isSettings
            ? this.settingsCurrentProject?.title
            : this.currentProjectTitle;
          await this.$store
            .dispatch('addMemberToProject', {
              membersToAdd: [
                {
                  user_id: member.id,
                  assigned_date: getCurrentDateInSeconds(),
                },
              ],
              projectId: this.addingTarget.id,
            })
            .then(() => {
              this.$store.dispatch('addUiNotification', {
                message: `${member.label} ${this.translate(
                  'addedTo'
                )} ${projectName} ${this.translate('space').toLowerCase()}`,
                duration: 4000,
              });
            })
            .catch(() =>
              this.getUser.id === member.id
                ? this.toggleMemberChecked(member, 'inProjectMembers')
                : this.toggleMemberChecked(member, 'notInProjectMembers')
            );
          break;
        case 'board':
          {
            const boardId = this.addingTarget.id;
            this.$store.dispatch('addMemberToBoard', {
              memberToAdd: {
                user_id: member.id,
                assigned_date: getCurrentDateInSeconds(),
              },
              boardId,
            });
          }
          break;
        case 'card':
        case this.$constants.routeNames.boardCard:
          {
            const delay = immediate ? 0 : undefined;
            this.assignMember(this.addingTarget.id, member.id, delay);
          }
          break;
        case this.$constants.quickCard:
        case this.$constants.quickChildCard: {
          this.$store.dispatch('setMembersToAddQuickCard', {
            value: {
              user_id: member.id,
              role: member.role,
              checked: true,
            },
            isModal: this.isQuickCardModal,
          });
          break;
        }
        case this.$constants.settingsTemplate.root: {
          this.$store.dispatch('setMembersToCardTemplate', {
            user_id: member.id,
            role: member.role,
            checked: true,
          });
          break;
        }
        default:
          break;
      }
    },

    canModifySpaceMember(member) {
      // ignore anything that's not for space membership
      if (!this.entityType.includes('project')) {
        return { canModify: true };
      }

      if (this.isGuestRole) {
        return { canModify: false, reason: this.translate('cannotModifyMembers') };
      }

      if (this.isInactiveMember(member.id) && !member.checked) {
        return { canModify: false, reason: this.translate('inactiveMembersCannotBeAdded') };
      }

      if (
        this.isCurrentUserSpaceOwner &&
        this.isMemberCurrentUser(member.id) &&
        this.members.length > 1
      ) {
        return { canModify: false, reason: this.translate('transferSpaceOwnershipShort') };
      }

      if (
        !this.isMemberCurrentUser(member.id) &&
        this.isProjectPrivate &&
        !(this.isCurrentUserSpaceOwner || (this.isElevated && this.isCurrentUserSpaceMember))
      ) {
        return { canModify: false, reason: this.translate('onlySpaceOwnerCanModifyMembers') };
      }

      if (this.isMemberSpaceOwner(member.id) && !this.isCurrentUserSpaceOwner) {
        return { canModify: false, reason: this.translate('cannotModifySpaceOwner') };
      }

      return { canModify: true };
    },

    displayInvitePopup() {
      if (this.isGuestRole) return;
      this.$store.dispatch('setMembersSearchInput', this.searchValue);
      this.handleCloseDropdown();
      this.$store.dispatch('setAddMemberPopup', true);
    },
    clearSearchInput() {
      this.searchValue = '';
    },

    orderMembers() {
      if (this.isEpic) {
        this.inProjectMembers = [...this.epicMembers];
        return;
      }

      const notInThisSpaceMembers = this.teamMembers.map((m) => ({
        ...m,
        groupingItem: true,
        groupLabel: this.translate('notInThisSpace'),
      }));

      const inThisSpaceMembers = [
        ...this.checkedProjectMembers,
        ...this.notCheckedProjectMembers,
      ].map((m) => ({
        ...m,
        groupingItem: true,
        groupLabel: this.translate('inThisSpace'),
      }));

      if (this.entityType.includes('project')) {
        this.inProjectMembers = [
          ...notInThisSpaceMembers,
          ...this.meFiltered,
          ...inThisSpaceMembers,
        ];
      } else {
        this.inProjectMembers = [
          ...this.meFiltered,
          ...inThisSpaceMembers,
          ...notInThisSpaceMembers,
        ];
      }
    },

    async memberClick(member, immediate = false) {
      this.cancelledRemoveMeUser = false;
      const { canModify, reason } = this.canModifySpaceMember(member);
      if (!canModify) {
        this.$store.dispatch('addUiNotification', {
          message: reason,
          status: this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
        return;
      }

      if (this.childCardItem || this.isAttendeesDropdown) {
        this.$emit('memberClick', member);
        const membersArray = 'inProjectMembers';
        this.toggleMemberChecked(member, membersArray);
        return;
      }
      if (this.singleSelect) {
        this.$emit('selected-item', member);
        return;
      }
      if (this.isGuestRole && !this.isGuestAddedToSpace) {
        this.$store.dispatch('addUiNotification', {
          message: this.translate('cantRemoveMembersFromSpace'),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
        return;
      }
      const memberChecked = member.checked;
      const membersArray = 'inProjectMembers';
      this.toggleMemberChecked(member, membersArray);

      if (memberChecked) {
        await this.removeMember(member, immediate);
        return;
      }
      this.addMembers(member, immediate);
    },

    toggleMemberChecked(member, membersArray) {
      const memberIndex = this[membersArray].findIndex((i) => i.id === member.id);
      if (memberIndex !== -1) {
        const isChecked = this[membersArray][memberIndex].checked;
        this[membersArray][memberIndex].checked = !isChecked;
      }
    },

    scrollHandle(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    getMemberDisplay(member) {
      return `${getMemberLabel(member)}`;
    },

    clickOutsideHandler(event) {
      if (this.isFocusElement()) return;
      this.$emit('clickOutside', event);
    },

    isMemberCurrentUser(memberId) {
      return this.getUser.id === memberId;
    },

    isMemberSpaceOwner(memberId) {
      return this.entityType.includes('project') && this.entity.user_id === memberId;
    },

    isInactiveMember(memberId) {
      return Object.keys(this.$store.getters.getInactiveTeamMembers[memberId] || {}).length > 0;
    },

    isInvited(memberId) {
      return this.invitees?.some?.((i) => i.id === memberId);
    },

    handleMoreClick(index) {
      this.currentMoreOptionsPopoverIndex = index;
      this.$refs.moreOptionsPopover[index]?.show();
    },

    getMemberKey(member) {
      // Some members don't have ids, so we use the email as the key
      return member.id || member.email;
    },
  },
};
</script>

<style lang="scss">
.list-wrap {
  width: 250px;
  height: auto;
  border-radius: 8px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.16));
  display: flex;
  flex-direction: column;
  max-height: 370px;
  will-change: filter;
  border: 1px solid;
  @apply bg-important-bg border-important-border;

  &.invite-members {
    max-height: unset;
    @apply w-[425px];

    .members-list {
      @apply max-h-[200px];
    }
  }

  &.hide-scroll-panel {
    .members-list .__panel {
      @apply pb-0;
    }
  }

  .members-header-wrap {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    padding: 12px 16px;
    border-bottom: 1px solid;
    align-items: center;
    @apply border-dividers-default;

    &.members-search {
      cursor: text;
    }

    &.invite-external {
      color: #707070;
      cursor: pointer;
    }

    .members-search-icon {
      max-width: 24px;
      min-width: 24px;
      overflow: hidden;
      color: #929292;
    }

    .members-header {
      border: none;
      box-sizing: border-box;
      outline: none;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.35px;
      width: 100%;
      @apply text-text-default bg-important-bg;

      &::placeholder {
        @apply text-text-placeholder;
      }
    }
  }

  .members-list {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.scrolling {
      .filter-dropdown-item {
        pointer-events: none;
      }
    }

    .__panel {
      padding-bottom: 4px;

      .members-notfound-wrap {
        background-color: #f5f5f5;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        margin: 6px 12px;
        padding: 12px;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0.35px;
        color: #2d3235;

        .members-notfound {
          color: #2d3235;
          text-decoration: underline;
          cursor: pointer;
        }
      }

      .filter-dropdown-label {
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0.35px;
        margin: 18px 16px 10px 16px;
        @apply text-text-placeholder;
      }
    }
  }
}
</style>
